import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, CanActivateFn, Route, UrlSegment, CanActivateChildFn, CanLoadFn, CanMatchFn } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TokenService } from '../shared/services/token.service';
import { AuthService } from '../shared/services/auth.service';
import { ApiUrls } from '../shared/api-url';
import { AppConfigService } from '../shared/services/app-config.service';

const SESSION_FLAG_USER = `ssoUserSaved`;
const DEV_BYPASS = false;
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {
  return handleAuthGuard(route, state.url);
};

export const authChildGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {
  return handleAuthGuard(route, state.url);
};


export const authMatchGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[]
) => {
  const url = '/' + segments.map(s => s.path).join('/');
  return handleAuthGuard({ queryParams: {} } as ActivatedRouteSnapshot, url);
};
function handleAuthGuard(route: ActivatedRouteSnapshot, url: string): Observable<boolean | UrlTree> | boolean | UrlTree {
  const http = inject(HttpClient);
  const tokenStore = inject(TokenService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const jwtHelper = inject(JwtHelperService);

  if (url.startsWith('/login') || url.startsWith('/unauthorized')) {
    return true;
  }

  const appConfig = inject(AppConfigService);
  const getAccessEndpoint = `${appConfig.masterBaseUrl.replace(/\/+$/, '')}/${ApiUrls.User.GetAccess.replace(/^\/+/, '')}`;

  const urlToken = route.queryParams['token'];
  const localStorageToken = tokenStore.token;
  const isSSOMode = tokenStore.isSingleSignOnMode;
  const ssoAccessToken = tokenStore.ssoAccessToken;

  if (urlToken) {
    const redirectUrl = localStorage.getItem('redirectUrl')
      || route.queryParams['redirectUrl']
      || url
      || '/';

    return processUrlTokenWithValidation(
      urlToken,
      redirectUrl,
      tokenStore,
      jwtHelper,
      router,
      http,
      getAccessEndpoint,
      authService
    );
  }

  if (localStorageToken || ssoAccessToken) {
    saveLastProtectedUrl(url);

    if (isSSOMode && ssoAccessToken) {
      ensureSsoUserSaved(tokenStore, jwtHelper);
      return validateAccessToken(tokenStore, http, authService
        , router, getAccessEndpoint);
    } else {
      return from(appConfig.loadCompanyConfiguration()).pipe(
        map(() => true)
      );
    }
  }

  if (!localStorageToken && ssoAccessToken) {
    return processSsoTokenWithValidation(
      ssoAccessToken,
      tokenStore,
      jwtHelper,
      router,
      http,
      getAccessEndpoint,
      authService,
      url
    );
  }

  if (DEV_BYPASS) {
    return true;
  }

  return redirectToPortal();
}

function saveLastProtectedUrl(url: string): void {
  if (url && url !== '/login' && url !== '/welcome') {
    localStorage.setItem('lastProtectedUrl', url);
  }
}

function processUrlTokenWithValidation(
  token: string,
  redirectUrl: string,
  tokenStore: TokenService,
  jwtHelper: JwtHelperService,
  router: Router,
  http: HttpClient,
  getAccessEndpoint: string,
  authService: AuthService
): Observable<boolean> {
  try {
    localStorage.setItem('redirectUrl', redirectUrl);
    tokenStore.setToken(token);

    const payload: any = jwtHelper.decodeToken(token);

    if (!payload) {
      router.navigateByUrl('/unauthorized');
      return throwError(() => new Error('Invalid token'));
    }

    tokenStore.saveUser(payload);

    return validateAccessToken(tokenStore, http, authService, router, getAccessEndpoint);
  } catch (e) {
    router.navigateByUrl('/unauthorized');
    return throwError(() => e);
  }
}

function processSsoTokenWithValidation(
  token: string,
  tokenStore: TokenService,
  jwtHelper: JwtHelperService,
  router: Router,
  http: HttpClient,
  getAccessEndpoint: string,
  authService: AuthService,
  attemptedUrl?: string
): Observable<boolean> {
  try {
    const decoded: any = jwtHelper.decodeToken(token);

    if (!decoded) {
      router.navigateByUrl('/unauthorized');
      return throwError(() => new Error('Invalid SSO token'));
    }

    let redirectUrl = localStorage.getItem('redirectUrl');

    if (!redirectUrl) {
      redirectUrl = localStorage.getItem('lastProtectedUrl');
      if (!redirectUrl || redirectUrl === '/' || redirectUrl === '/login') {
        redirectUrl = attemptedUrl && attemptedUrl !== '/login' ? attemptedUrl : '/dashboard';
      }
      localStorage.setItem('redirectUrl', redirectUrl);
    }


    const id =
      decoded.EmpNo ??
      decoded.nameid ??
      decoded.UserID ??
      decoded.sid ??
      decoded.sub ??
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'] ??
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
      null;

    const userData = {
      nameid: id,
      UserID: id,
      fullName: decoded.unique_name ?? decoded.name ?? '',
      userPortals: decoded.userPortals ?? [],
    };

    tokenStore.saveUser(userData);

    ensureSsoUserSaved(tokenStore, jwtHelper);

    return validateAccessToken(tokenStore, http, authService, router, getAccessEndpoint);
  } catch (e) {
    router.navigateByUrl('/unauthorized');
    return throwError(() => e);
  }
}

function validateAccessToken(
  tokenStore: TokenService,
  http: HttpClient,
  authService: AuthService,
  router: Router,
  getAccessEndpoint: string
): Observable<boolean> {
  const token = tokenStore.ssoAccessToken || tokenStore.token;

  if (!token) {
    const appConfig = inject(AppConfigService);
    window.location.href = appConfig.portalUrl;
  }

  return http.post(getAccessEndpoint, { token }).pipe(
    map((response: any) => {
      if (response?.Status === false) {
        throw new HttpErrorResponse({
          error: response,
          status: 401,
          statusText: 'API Error',
          url: getAccessEndpoint
        });
      }

      const userId = response?.enhancedUserDetails?.userId;
      const roleId = response?.enhancedUserDetails?.roles?.roleId;
      const roleName =
        response?.enhancedUserDetails?.roles?.roleName ||
        response?.enhancedUserDetails?.roles?.name;

      if (userId) localStorage.setItem('userId', userId.toString());
      if (roleId) localStorage.setItem('roleId', roleId.toString());
      if (roleName) localStorage.setItem('roleName', roleName);

      if (roleId && roleName) {
        localStorage.setItem('trole', JSON.stringify({ roleId, name: roleName }));
      }

      return true;
    }),
    catchError(err => {
      return handleAccessError(
        err,
        tokenStore,
        authService,
        router,
        getAccessEndpoint,
        http
      );
    })
  );
}

function handleAccessError(
  err: any,
  tokenStore: TokenService,
  authService: AuthService,
  router: Router,
  getAccessEndpoint: string,
  http: HttpClient
): Observable<boolean> {
  if (err instanceof HttpErrorResponse && err.status === 401 && tokenStore.isSingleSignOnMode) {
    const refreshToken = tokenStore.ssoRefreshToken;
    if (!refreshToken) {
      const appConfig = inject(AppConfigService);
      window.location.href = appConfig.hubUrl;
    }
    return refreshAndRetry(authService, tokenStore, router, getAccessEndpoint, http);
  }

  if (err instanceof HttpErrorResponse && err.status === 401 && tokenStore.isSingleSignOnMode) {
    const refreshToken = tokenStore.ssoRefreshToken;
    if (!refreshToken) {
      const appConfig = inject(AppConfigService);
      window.location.href = appConfig.hubUrl;
      return throwError(() => new Error('No refresh token'));
    }

    clearTimeout((window as any).__loginRedirectTimer);

    return refreshAndRetry(authService, tokenStore, router, getAccessEndpoint, http);
  }

  return throwError(() => err);
}
function refreshAndRetry(
  authService: AuthService,
  tokenStore: TokenService,
  router: Router,
  getAccessEndpoint: string,
  http: HttpClient
): Observable<boolean> {
  const { isRefreshing, tokenSubject } = authService.getRefreshState();

  if (!isRefreshing) {
    const refreshToken = tokenStore.ssoRefreshToken!;
    return authService.refreshToken(refreshToken).pipe(
      map(() => true),
      catchError(e => {
        const appConfig = inject(AppConfigService);
        window.location.href = appConfig.hubUrl;
        return throwError(() => e);
      })
    );
  }

  return tokenSubject.pipe(
    filter(t => t !== null),
    take(1),
    map(() => true)
  );
}

function ensureSsoUserSaved(tokenStore: TokenService, jwtHelper: JwtHelperService): void {
  if (DEV_BYPASS || !tokenStore.isSingleSignOnMode || sessionStorage.getItem(SESSION_FLAG_USER) === '1') {
    return;
  }

  const token = tokenStore.ssoAccessToken;
  if (!token) {
    return;
  }

  const decoded: any = jwtHelper.decodeToken(token);

  if (!decoded) {
    return;
  }

  const id =
    decoded.EmpNo ??
    decoded.nameid ??
    decoded.UserID ??
    decoded.sid ??
    decoded.sub ??
    decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'] ??
    decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
    null;

  const user = {
    nameid: id,
    UserID: id,
    fullName: decoded.unique_name ?? decoded.name ?? '',
    userPortals: decoded.userPortals ?? [],
  };

  tokenStore.saveUser(user);
  sessionStorage.setItem(SESSION_FLAG_USER, '1');
}

function redirectToPortal(): UrlTree | boolean {
  const appConfig = inject(AppConfigService);
  const portalLoginUrl = appConfig.portalUrl;
  window.location.href = portalLoginUrl;
  return false;
}